import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import type { TenantFlagSummary } from '@zag/feature-flags/common';
import { Avatar, Card, ZagButton, ZagSwitch } from '@zag/ui';

export interface TenantFlagEnabledToggle {
  readonly flag: TenantFlagSummary;
  readonly enabled: boolean;
}

/**
 * Displays a tenant's feature-flag rollout on the tenant details
 * page — mirrors `<zag-tenant-active-licences>` from
 * `@zag/licensing/ui`. Each card carries an enable/disable switch
 * in its title so admins flip whole flags on/off without touching
 * the definition; features shown below are read-only tags.
 * Presentational — the host resolves which TenantFlagSummary rows
 * to render and handles user-picker navigation.
 */
@Component({
  selector: 'zag-tenant-active-flags',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Avatar, Card, TranslatePipe, ZagButton, ZagSwitch],
  template: `
    <div class="wrap">
      <header class="wrap__header">
        <div>
          <h3 class="wrap__title">{{ 'control.featureFlags.tenantActive.title' | translate }}</h3>
          <p class="wrap__subtitle">
            {{ 'control.featureFlags.tenantActive.subtitle' | translate: { enabled: enabledCount(), total: flags().length } }}
          </p>
        </div>
      </header>

      @if (flags().length === 0) {
        <div class="empty">
          <p class="empty__title">{{ 'control.featureFlags.tenantActive.empty.title' | translate }}</p>
          <p class="empty__hint">{{ 'control.featureFlags.tenantActive.empty.hint' | translate }}</p>
        </div>
      } @else {
        <ul class="cards" role="list">
          @for (flag of flags(); track flag.flagKey) {
            <li>
              <zag-card variant="elevated">
                <div card-header class="card-header">
                  <zag-avatar class="card-header__avatar" [name]="flag.name" size="md" variant="warning" />
                  <div class="identity">
                    <span class="identity__key">{{ flag.flagKey }}</span>
                    <h4 class="identity__name">{{ flag.name }}</h4>
                  </div>
                  <div class="card-header__toggle">
                    <zag-switch
                      [checked]="flag.enabled"
                      [disabled]="!canManage()"
                      [ariaLabel]="
                        'control.featureFlags.tenantActive.toggleAria'
                          | translate
                            : {
                                name: flag.name,
                                state:
                                  (flag.enabled
                                    ? 'control.featureFlags.tenantActive.statusEnabled'
                                    : 'control.featureFlags.tenantActive.statusDisabled'
                                  ) | translate,
                              }
                      "
                      (checkedChange)="enabledToggled.emit({ flag, enabled: $event })"
                    />
                  </div>
                </div>

                @if (flag.description) {
                  <p class="description">{{ flag.description }}</p>
                }

                <div class="features">
                  @if (flag.features.length === 0) {
                    <span class="feature-tag feature-tag--empty">
                      {{ 'control.featureFlags.definitions.card.noFeatures' | translate }}
                    </span>
                  } @else {
                    @for (feature of flag.features; track feature) {
                      <span class="feature-tag">{{ feature }}</span>
                    }
                  }
                </div>

                <div card-footer class="card-footer">
                  <span class="users">{{ usersLabels().get(flag.flagKey) }}</span>
                  @if (canManage()) {
                    <button type="button" zagButton="basic" zagType="secondary" zagSize="sm" (click)="manageUsers.emit(flag)">
                      {{ 'control.featureFlags.tenantActive.manageUsers' | translate }}
                    </button>
                  }
                </div>
              </zag-card>
            </li>
          }
        </ul>
      }
    </div>
  `,
  styleUrl: './tenant-active-flags.component.scss',
})
export class TenantActiveFlagsComponent {
  private readonly translate = inject(TranslateService);

  readonly flags = input<readonly TenantFlagSummary[]>([]);
  readonly canManage = input<boolean>(true);

  readonly enabledToggled = output<TenantFlagEnabledToggle>();
  readonly manageUsers = output<TenantFlagSummary>();

  protected readonly enabledCount = computed(() => this.flags().filter(f => f.enabled).length);

  // Re-runs `translate.instant()` in usersLabels whenever any translation
  // event fires (see AGENTS.md pattern in @zag/i18n).
  private readonly translationChange = toSignal(
    merge(this.translate.onLangChange, this.translate.onDefaultLangChange, this.translate.onTranslationChange),
    { initialValue: null }
  );

  protected readonly usersLabels = computed(() => {
    this.translationChange();
    const map = new Map<string, string>();
    for (const flag of this.flags()) {
      map.set(flag.flagKey, this.buildUsersLabel(flag));
    }
    return map;
  });

  private buildUsersLabel(flag: TenantFlagSummary): string {
    if (flag.userIds.length === 0 || flag.userIds.includes(null)) {
      return this.translate.instant('control.featureFlags.tenantActive.usersLabel.all');
    }
    return this.translate.instant('control.featureFlags.tenantActive.usersLabel.count', { count: flag.userIds.length });
  }
}
